import { BatteryModel, BatteryOverviewDto, fromDtoToModel } from 'src/Libs/batteries'
import { MeasurementBoundaries } from 'src/Libs/api'
import { useEffect, useState } from 'react'
import { mergeBatteries } from 'src/Libs/batteries/utils'
import { useWebSocketApi } from '@e3dc-react/shell/Hooks'
import { isUndefined } from 'lodash'

const SERVICE_ID = '[BATTERY HARDWARE]'

const DEFAULT_BOUNDARIES = { max: 0, min: 0, status: 0, target: 0 }
const DEFAULT_MEASUREMENT = { max: 0, min: 0, values: [0], count: 0 } as MeasurementBoundaries

const DEFAULT_BATTERY_SETTINGS = {
  batteryListIndex: 0,
  voltageAdjustment: DEFAULT_BOUNDARIES,
  deviceState: { connected: false, working: false, inService: false },
  voltage: DEFAULT_MEASUREMENT,
  dcbType: '',
  terminalVoltage: 0,
  temperature: DEFAULT_MEASUREMENT,
  current: DEFAULT_MEASUREMENT,
  statusCode: '',
  maxDischargeCurrent: 0,
  maxChargeCurrent: 0,
  eodVoltage: 0,
  cellVoltage: [0],
  maxVoltage: 0,
  errorCode: '',
  chargeCycles: 0,
  firmwareVersion: '',
  deviceName: '',
  rsoc: 0,
  asoc: 0,
  countModules: 0,
  capacityRemaining: 0,
  capacityTotal: 0,
} as BatteryModel

/**
 * This hook provides the utils for accessing the Battery settings and overviews through a websocket connection.
 * The returning information coming directly from the connected device.
 *
 * It always returns a valid model, containing at first with initial default data and in the followup with the last
 * valid fetched data, to ensure data stability.
 *
 * @param storageId
 * @param batteryIndex
 */
const useLiveBattery = (storageId: string | undefined, batteryIndex?: string): BatteryModel => {
  const [batterySettings, setBatterySettings] = useState(DEFAULT_BATTERY_SETTINGS)
  const resourcePath = `storages/${storageId}/hardware/batteries/overview/${batteryIndex}/ws`
  const { socketState } = useWebSocketApi<BatteryModel, BatteryOverviewDto>({
    apiResourcePath: resourcePath,
    loggerKey: SERVICE_ID,
    transformer: fromDtoToModel,
    doNotInitialize: isUndefined(storageId) || isUndefined(batteryIndex) || isNaN(parseInt(batteryIndex)),
    endpoint: process.env.REACT_APP_API_ENDPOINT ?? '',
  })

  useEffect(() => {
    if (socketState.data) {
      const mutated = mergeBatteries(socketState.data, DEFAULT_BATTERY_SETTINGS)
      setBatterySettings(mutated)
    }
  }, [socketState.data])

  return batterySettings
}

export default useLiveBattery
